import React, { useEffect, useRef, useState } from 'react';
import { Asset as ContentfulAsset } from 'contentful';
import Image from 'next/image';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import axios from 'axios';
import logger from '@greenlightme/logger';
import { captureError, trackRumEvent } from '../../utils/datadogRum';
import { useInView } from 'react-intersection-observer';
import { Asset as MediaAsset } from '../AtomicComponents/Asset';

interface AppFeatureAnimationItemProps {
  media: ContentfulAsset;
  play?: boolean;
  onEnded?: () => void;
}

export function AppFeatureAnimationItem({
  media,
  play,
  onEnded
}: AppFeatureAnimationItemProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [lottieData, setLottieData] = useState(null);
  const [played, setPlayed] = useState(false);
  const contentType = media.fields.file.contentType;
  const [containerRef] = useInView({
    triggerOnce: true,
    threshold: 0.25
  });

  const assetVideoRef = useRef<HTMLVideoElement | null>(null);
  const prevPlayRef = useRef<boolean | undefined>(play);
  const hasAttemptedPlayRef = useRef(false);
  const [videoReady, setVideoReady] = useState(false);

  const handleEnded = () => {
    onEnded?.();
    setPlayed(true);
  };

  useEffect(() => {
    if (contentType.includes('json') && play && !lottieData) {
      axios
        .get(media.fields.file.url)
        .then(({ data }) => setLottieData(data))
        .catch((err) => {
          logger.error('Error fetching lottie data', err);
          console.error('Error fetching lottie data', err);
        });
    }
  }, [contentType, play, lottieData, media.fields.file.url]);

  useEffect(() => {
    const videoEl = assetVideoRef.current;
    const lottie = lottieRef.current;
    const playChanged = prevPlayRef.current !== play;
    prevPlayRef.current = play;

    if (videoEl && videoReady) {
      if (play) {
        if (played) {
          videoEl.currentTime = 0;
          setPlayed(false);
          hasAttemptedPlayRef.current = false;
        }
        // Play if: play just changed to true, or we need to restart after playing, or video just became ready and we haven't attempted play yet
        if (
          playChanged ||
          played ||
          (!hasAttemptedPlayRef.current && !played)
        ) {
          hasAttemptedPlayRef.current = true;
          const playPromise = videoEl.play();
          if (playPromise !== undefined) {
            playPromise.catch((err: DOMException) => {
              if (err.name === 'NotAllowedError') {
                trackRumEvent('autoplay_blocked', {
                  page_url: window.location.href,
                  component: 'AppFeatureAnimationItem'
                });
              }
              logger.error('Error playing video', err);
              console.error('Error playing video', err);
              captureError(err);
            });
          }
        }
      } else {
        videoEl.pause();
        setPlayed(false);
        hasAttemptedPlayRef.current = false;
      }
    }

    if (lottie) {
      if (play) {
        if (played) {
          lottie.goToAndStop(0);
        }
        if (playChanged || played) {
          lottie.play();
        }
      } else {
        lottie.stop();
      }
    }
  }, [play, played, videoReady]);

  if (contentType.includes('image')) {
    const imgWidth = media.fields.file.details.image?.width;
    const imgHeight = media.fields.file.details.image?.height;
    const aspectRatio =
      imgWidth && imgHeight ? `${imgWidth}/${imgHeight}` : undefined;
    const rawUrl = media.fields.file.url;
    const httpsUrl = rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`;
    const lqipUrl = `${httpsUrl}?w=24&q=20&fm=jpg`;
    return (
      <div
        ref={containerRef}
        className="relative flex w-full items-center justify-center tablet:h-full desktop:h-full"
        style={
          aspectRatio
            ? {
                aspectRatio: aspectRatio
              }
            : {}
        }
      >
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <Image
            src={lqipUrl}
            alt=""
            width={imgWidth}
            height={imgHeight}
            className="absolute inset-0 h-full w-full object-contain"
            style={{ filter: 'blur(12px)' }}
            loader={({ src }) => src}
            unoptimized={true}
            data-testid="app-feature-animation-image-placeholder"
          />
        </div>
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <Image
            src={media.fields.file.url}
            alt={media.fields.title}
            width={imgWidth}
            height={imgHeight}
            className={`h-full w-full object-contain opacity-0 transition-opacity duration-500`}
            loader={({ src }) => src}
            unoptimized={true}
            loading="lazy"
            onLoadingComplete={(img: HTMLImageElement) => {
              img.classList.remove('opacity-0');
              img.classList.add('opacity-100');
            }}
            data-testid="app-feature-animation-image"
          />
        </div>
      </div>
    );
  }

  if (contentType.includes('video')) {
    return (
      <div
        className="flex h-full w-full items-center justify-center [&>div>video]:object-contain"
        data-testid="app-feature-animation-video"
      >
        <MediaAsset
          asset={media}
          className="h-full w-full"
          muted
          controls={false}
          disableViewportManagement
          onVideoRef={(el) => {
            // Wire up ended handler once
            if (assetVideoRef.current !== el) {
              if (assetVideoRef.current) {
                assetVideoRef.current.removeEventListener('ended', handleEnded);
              }
              assetVideoRef.current = el;
              if (el) {
                el.addEventListener('ended', handleEnded);
                setVideoReady(true);
              } else {
                setVideoReady(false);
                hasAttemptedPlayRef.current = false;
              }
            }
          }}
        />
      </div>
    );
  }

  if (contentType.includes('json')) {
    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={lottieData}
        autoPlay={false}
        loop={false}
        onComplete={handleEnded}
        className="h-full w-full object-fill"
        data-testid="app-feature-animation-lottie"
      />
    );
  }

  return null;
}
